import { filterClasses } from "@swivl-mono/helpers";
import { useState } from "react";
import { IoChevronDown, IoChevronForward, IoHardwareChipOutline } from "react-icons/io5";

import { CodeBlock } from "./CodeBlock";
import { InvisibleRow, SECTION_LABEL } from "./InvisibleRow";

interface LLMCallCardProps {
  content: string;
  metadata?: Record<string, any>;
  /** Optional: full LLM call record fetched on demand */
  fullRecord?: any;
  /** Called when the card is expanded and the full record is needed */
  onRequestFullRecord?: () => void;
  createdAt?: string;
}

/**
 * One model call. Collapsed to the shared {@link InvisibleRow} pill with the
 * model, token counts and latency in the label; the full record (system prompt,
 * messages, tool calls, response) loads on first expand.
 */
export function LLMCallCard({ content, metadata, fullRecord, onRequestFullRecord, createdAt }: LLMCallCardProps): React.ReactElement {
  const meta = metadata || {};
  const hasToolErrors = fullRecord?.toolCalls?.some((tc: any) => !tc.success) ?? false;

  const label = (
    <>
      LLM call #{meta.loopIndex || "?"} · {meta.model} · {meta.inputTokens || 0}in/{meta.outputTokens || 0}out ·{" "}
      {meta.firstTokenLatencyMs !== undefined ? `TTFT ${meta.firstTokenLatencyMs}ms · ` : ""}
      {meta.latencyMs || 0}ms
      {meta.stopReason ? ` · ${meta.stopReason}` : ""}
      {hasToolErrors && <span className="body-2xs ml-1.5 rounded-sm bg-red-50 px-1.5 py-0.5 text-red-700">tool error</span>}
    </>
  );

  return (
    // Admin-tier row (ADMIN_ONLY_MESSAGE_TYPES), hence the purple fill.
    <InvisibleRow
      tone="purple"
      icon={<IoHardwareChipOutline size={16} />}
      label={label}
      createdAt={createdAt}
      onExpand={() => {
        if (!fullRecord) onRequestFullRecord?.();
      }}
    >
      <div className="text-left">
        {!fullRecord && <p className="body-mono text-grey-500">Detailed record not available</p>}

        {fullRecord && (
          <>
            {fullRecord.systemPrompt && (
              <ExpandableSection title="System Prompt">
                {fullRecord.systemPrompt.sections?.map((section: any) => (
                  <div key={section.id} className="mb-2">
                    <p className={SECTION_LABEL}>{section.id}</p>
                    <CodeBlock>{section.text}</CodeBlock>
                  </div>
                ))}
                {!fullRecord.systemPrompt.sections && fullRecord.systemPrompt.staticContent && (
                  <div className="mb-2">
                    <p className={SECTION_LABEL}>Static (Cached)</p>
                    <CodeBlock>{fullRecord.systemPrompt.staticContent}</CodeBlock>
                  </div>
                )}
                {!fullRecord.systemPrompt.sections && fullRecord.systemPrompt.dynamicContent && (
                  <div>
                    <p className={SECTION_LABEL}>Dynamic (Per-call)</p>
                    <CodeBlock>{fullRecord.systemPrompt.dynamicContent}</CodeBlock>
                  </div>
                )}
              </ExpandableSection>
            )}

            {fullRecord.responseText && (
              <div className="mb-3">
                <p className={SECTION_LABEL}>Response Text</p>
                <CodeBlock>{fullRecord.responseText}</CodeBlock>
              </div>
            )}

            {fullRecord.toolCalls && fullRecord.toolCalls.length > 0 && (
              <div className="mb-3">
                <p className={SECTION_LABEL}>Tool Calls ({fullRecord.toolCalls.length})</p>
                {fullRecord.toolCalls.map((tc: any, i: number) => (
                  // A failed call is the only thing here that gets colour: the
                  // successful ones are a record, the failed one is a finding.
                  <div
                    key={i}
                    className={filterClasses("mt-2 rounded-sm border p-2", tc.success ? "border-grey-200 bg-white" : "border-red-200 bg-red-50")}
                  >
                    <div className="flex items-center gap-2">
                      <span className="body-mono-alt text-grey-700">{tc.name}</span>
                      <span className={filterClasses("size-1.5 rounded-full", tc.success ? "bg-teal-500" : "bg-red-500")} />
                      {tc.latencyMs > 0 && <span className="body-mono text-grey-600">{tc.latencyMs}ms</span>}
                    </div>
                    <CodeBlock>{JSON.stringify(tc.input, null, 2)}</CodeBlock>
                    {tc.success && tc.result && (
                      <div className="mt-2">
                        <p className={SECTION_LABEL}>Result</p>
                        <CodeBlock>{formatToolResult(tc.result)}</CodeBlock>
                      </div>
                    )}
                    {!tc.success && tc.result && (
                      <div className="mt-2">
                        <p className={SECTION_LABEL}>Error</p>
                        <p className="body-mono mt-1 rounded-sm border border-red-200 bg-red-50 p-2 text-left wrap-break-word whitespace-pre-wrap text-red-700">
                          {typeof tc.result === "string" ? tc.result : JSON.stringify(tc.result, null, 2)}
                        </p>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            )}

            {fullRecord.toolContext && (
              <div className="mb-3">
                <p className={SECTION_LABEL}>Tool Context</p>
                <div className="mt-1 flex flex-wrap justify-center gap-1">
                  {TOOL_CONTEXT_FIELDS.map(([key, field]) =>
                    fullRecord.toolContext[field] ? (
                      <span key={field} className="body-mono rounded-sm bg-grey-50 px-2 py-1 text-grey-600">
                        {key}: {fullRecord.toolContext[field]}
                      </span>
                    ) : null
                  )}
                </div>
              </div>
            )}

            {fullRecord.toolDefinitions && fullRecord.toolDefinitions.length > 0 && (
              <ExpandableSection title={`Available Tools (${fullRecord.toolDefinitions.length})`}>
                {fullRecord.toolDefinitions.map((td: any, i: number) => (
                  <div key={i} className="mb-1 rounded-sm border border-grey-200 bg-white p-2">
                    <p className="body-mono-alt text-grey-700">{td.name}</p>
                    {td.description && <p className="body-mono text-grey-600">{td.description}</p>}
                  </div>
                ))}
              </ExpandableSection>
            )}
          </>
        )}
      </div>
    </InvisibleRow>
  );
}

/** Context fields we surface, as [label, key], in a fixed order. */
const TOOL_CONTEXT_FIELDS: [string, string][] = [
  ["location", "locationId"],
  ["unit", "unitId"],
  ["lease", "leaseId"],
  ["channel", "channel"],
  ["user", "userId"]
];

function formatToolResult(result: unknown): string {
  if (typeof result === "string") {
    try {
      return JSON.stringify(JSON.parse(result), null, 2);
    } catch {
      return result;
    }
  }
  return JSON.stringify(result, null, 2);
}

function ExpandableSection({ title, children }: { title: string; children: React.ReactNode }): React.ReactElement {
  const [open, setOpen] = useState(false);

  return (
    <div className="mb-3">
      <button type="button" className="flex cursor-pointer items-center gap-1.5 text-grey-500" onClick={() => setOpen(!open)}>
        {open ? <IoChevronDown size={10} /> : <IoChevronForward size={10} />}
        <p className={SECTION_LABEL}>{title}</p>
      </button>
      {open && <div className="mt-1">{children}</div>}
    </div>
  );
}
